import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import SearchableSelect from '@/components/common/SearchableSelect';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import { Building2, Mail, Save, Loader2 } from 'lucide-react';
import BillingDetailsSection from '@/components/billing/BillingDetailsSection';
import { useClientScope } from '@/context/ClientScopeProvider';
import { useState, useEffect } from 'react';
interface AdvisorClient {
  id: string;
  company_name: string;
  contact_name: string;
  contact_email: string;
  systems: Array<{ name: string; type: string; role: string }>;
  status: string;
  status_display: string;
  created_at: string;
  updated_at: string;
}

const STATUS_OPTIONS = [
  { value: 'lead', label: 'Lead' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'proposal_sent', label: 'Proposal Sent' },
  { value: 'onboarded', label: 'Onboarded' },
];

export default function ClientInfoPage() {
  const { id } = useParams<{ id: string }>();
  const { orgId } = useClientScope();
  const queryClient = useQueryClient();

  const { data: client, isLoading } = useQuery<AdvisorClient>({
    queryKey: ['advisor-client', id],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.get(`/advisor/clients/${id}/`);
      return res.data;
    },
    enabled: !!id,
  });

  const [fields, setFields] = useState({
    company_name: '',
    contact_name: '',
    contact_email: '',
    status: 'lead',
  });
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (client) {
      setFields({
        company_name: client.company_name || '',
        contact_name: client.contact_name || '',
        contact_email: client.contact_email || '',
        status: client.status || 'lead',
      });
      setDirty(false);
    }
  }, [client]);

  const updateMutation = useMutation({
    mutationFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return api.patch(`/advisor/clients/${id}/`, fields);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['advisor-client', id] });
      queryClient.invalidateQueries({ queryKey: ['advisor-client-nav', id] });
      setDirty(false);
    },
  });

  const set = (key: string, value: string) => {
    setFields((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
      </div>
    );
  }

  if (!client) {
    return <div className="p-8 text-muted-foreground">Client not found.</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Client Settings</h2>
        <p className="text-muted-foreground">Contacts and billing details</p>
      </div>

      {updateMutation.isSuccess && !dirty && (
        <div className="text-sm text-green-600 bg-green-50 dark:bg-green-950/20 rounded-md px-3 py-2">
          Changes saved successfully.
        </div>
      )}

      {/* Company Information */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Building2 className="h-4 w-4 text-blue-500" />
            Company Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Company Name</label>
              <Input value={fields.company_name} onChange={(e) => set('company_name', e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Status</label>
              <SearchableSelect
                value={fields.status}
                onChange={(value) => set('status', value)}
                placeholder="Status"
                searchPlaceholder="Search statuses…"
                options={STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                testId="client-status-select"
              />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                <Mail className="h-3 w-3" /> Contact Name
              </label>
              <Input value={fields.contact_name} onChange={(e) => set('contact_name', e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                <Mail className="h-3 w-3" /> Contact Email
              </label>
              <Input type="email" value={fields.contact_email} onChange={(e) => set('contact_email', e.target.value)} />
            </div>
          </div>
        </CardContent>
      </Card>

      {orgId ? (
        <BillingDetailsSection orgId={orgId} whose={`${client.company_name}'s`} onBehalfOf={client.company_name} />
      ) : (
        <p className="text-sm text-muted-foreground" data-testid="client-billing-later">
          Billing details appear here once {client.company_name} joins BitSwan and enters them.
        </p>
      )}

      {/* Save at the bottom of the form */}
      {dirty && (
        <div className="flex items-center gap-3">
          <Button onClick={() => updateMutation.mutate()} disabled={updateMutation.isPending}>
            {updateMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Save Changes
          </Button>
          {updateMutation.isSuccess && <span className="text-sm text-green-600">Saved</span>}
        </div>
      )}
    </div>
  );
}
